import React, { useState } from 'react';
import {
  ArrowLeft,
  Lock,
  FileText,
  Database,
  HardDrive,
  Share2,
  UserCheck,
  Cookie,
  AlertTriangle,
  Mail,
  ShieldCheck,
  Eye,
  EyeOff,
  Clock,
  ListChecks
} from 'lucide-react';

const PoliticaPrivacidad = () => {
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('introduccion');

  // Secciones de la política de privacidad
  const sections = [
    {
      id: 'introduccion',
      title: 'Introducción',
      icon: FileText
    },
    {
      id: 'informacion-recopilada',
      title: 'Información Recopilada',
      icon: Database
    },
    {
      id: 'uso-informacion',
      title: 'Uso de la Información',
      icon: ListChecks
    },
    {
      id: 'seguridad',
      title: 'Seguridad de la Información',
      icon: ShieldCheck
    },
    {
      id: 'compartir',
      title: 'Compartir Información',
      icon: Share2
    },
    {
      id: 'derechos',
      title: 'Derechos de los Usuarios',
      icon: UserCheck
    },
    {
      id: 'cookies',
      title: 'Cookies y Tecnologías',
      icon: Cookie
    },
    {
      id: 'cambios',
      title: 'Cambios en la Política',
      icon: Clock
    },
    {
      id: 'contacto',
      title: 'Información de Contacto',
      icon: Mail
    }
  ];

  const handleSelectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBackToRegister = () => {
    // En un entorno real, esto usaría el router para volver a la página anterior
    alert('Regresar a registro');
    // window.history.back();
  };

  const handleContinue = () => {
    if (acceptedPolicy) {
      alert('Política de privacidad aceptada - Continuando con el registro');
      // Aquí redirigirías o actualizarías el estado
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-pink-50 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-100 to-transparent opacity-40"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-gradient-to-t from-pink-100 to-transparent opacity-40"></div>
        
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-blue-200 opacity-20 blur-xl"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-purple-200 opacity-20 blur-xl"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 rounded-full bg-emerald-200 opacity-20 blur-xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button 
            onClick={handleBackToRegister}
            className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            <span>Volver al registro</span>
          </button>
          
          <div className="flex items-center gap-2">
            <img 
              src="/InKairos.ico" 
              alt="InKayros Logo" 
              className="w-9 h-9"
            />
            <h1 className="text-2xl font-bold text-gray-800">InKayros.com</h1>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden border border-gray-100">
          {/* Title bar */}
          <div className="bg-blue-600 text-white p-6">
            <div className="flex items-center gap-3">
              <Lock className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Política de Privacidad</h2>
            </div>
            <p className="text-blue-100 mt-2">Última actualización: Marzo 2025</p>
          </div>

          <div className="flex flex-col lg:flex-row">
            {/* Sidebar navigation */}
            <div className="lg:w-72 border-r border-gray-200 bg-gray-50">
              <div className="p-4">
                <h3 className="font-medium text-gray-700 mb-3">Contenido</h3>
                <nav className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => handleSelectSection(section.id)}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${
                          activeSectionId === section.id
                            ? 'bg-blue-50 text-blue-700 font-medium border-l-4 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={18} className={activeSectionId === section.id ? 'text-blue-600' : 'text-gray-400'} />
                        <span>{section.title}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 p-6 lg:p-8 max-w-4xl overflow-y-auto">
              <div className="prose max-w-full">
                <section id="introduccion">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-blue-600" />
                    Introducción
                  </h2>
                  <p className="text-gray-700">
                    En InKayros.com (en adelante, "InKayros", "nosotros", "nuestro") valoramos y respetamos su privacidad. 
                    Esta Política de Privacidad describe cómo recopilamos, utilizamos, almacenamos y protegemos la información 
                    que usted nos proporciona al utilizar nuestra plataforma inmobiliaria.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Al registrarse, acceder o utilizar nuestros servicios, usted acepta las prácticas descritas en esta Política 
                    de Privacidad. Le recomendamos leer cuidadosamente este documento para comprender nuestras políticas y 
                    prácticas respecto a sus datos personales y cómo los trataremos.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Esta Política de Privacidad se aplica a toda la información recopilada a través de nuestra plataforma web, 
                    aplicaciones móviles, comunicaciones por correo electrónico, formularios de contacto y cualquier otro medio 
                    relacionado con nuestros servicios.
                  </p>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="informacion-recopilada">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Database className="h-6 w-6 text-blue-600" />
                    Información que Recopilamos
                  </h2>
                  
                  <div className="rounded-xl overflow-hidden border border-gray-200 mb-6">
                    <div className="bg-blue-50 px-6 py-4 flex items-center gap-3">
                      <Eye className="h-5 w-5 text-blue-600" />
                      <h3 className="text-lg font-semibold text-blue-800">Datos Públicos</h3>
                    </div>
                    <div className="p-6 bg-white">
                      <p className="text-gray-700">Recopilamos y hacemos públicos los siguientes datos:</p>
                      <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                        <li>
                          <strong>Detalles de los inmuebles</strong>: dirección, tipo de propiedad, características, 
                          precio, dimensiones, imágenes, videos y descripciones proporcionadas por los anunciantes.
                        </li>
                        <li>
                          <strong>Información de contacto del anunciante</strong>: nombre, teléfono, correo electrónico, 
                          según lo que el anunciante decida hacer público en su perfil o anuncios.
                        </li>
                        <li>
                          <strong>Reseñas y calificaciones</strong>: opiniones públicas que los usuarios decidan compartir 
                          sobre inmuebles o anunciantes.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="rounded-xl overflow-hidden border border-gray-200">
                    <div className="bg-indigo-50 px-6 py-4 flex items-center gap-3">
                      <EyeOff className="h-5 w-5 text-indigo-600" />
                      <h3 className="text-lg font-semibold text-indigo-800">Datos Privados</h3>
                    </div>
                    <div className="p-6 bg-white">
                      <p className="text-gray-700">Recopilamos y mantenemos privados los siguientes datos:</p>
                      <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                        <li>
                          <strong>Interacciones de los usuarios</strong>: mensajes entre usuarios, búsquedas guardadas, propiedades 
                          marcadas como favoritas, historial de visualizaciones.
                        </li>
                        <li>
                          <strong>Datos de contacto adicionales</strong>: información de contacto que los usuarios proporcionen 
                          de manera privada durante el registro o actualización de perfil.
                        </li>
                        <li>
                          <strong>Datos de registro</strong>: dirección IP, tipo de navegador, información del dispositivo, 
                          fecha y hora de acceso, páginas visitadas.
                        </li>
                        <li>
                          <strong>Datos de autenticación</strong>: credenciales de acceso (almacenadas de forma encriptada), 
                          registros de inicio de sesión.
                        </li>
                        <li>
                          <strong>Información financiera</strong>: en caso de transacciones premium, datos de facturación y medios de pago 
                          (procesados a través de pasarelas de pago seguras y nunca almacenados completamente en nuestros servidores).
                        </li>
                        <li>
                          <strong>Información estadística</strong>: datos agregados sobre el uso de la plataforma, comportamiento de los 
                          usuarios y efectividad de los anuncios.
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="uso-informacion">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <ListChecks className="h-6 w-6 text-blue-600" />
                    Uso de la Información
                  </h2>
                  <p className="text-gray-700">
                    La información que recopilamos se utiliza para los siguientes propósitos:
                  </p>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Datos públicos</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>
                      <strong>Mostrar los anuncios de inmuebles</strong> en la plataforma para facilitar la búsqueda y 
                      comparación de propiedades.
                    </li>
                    <li>
                      <strong>Facilitar el contacto</strong> entre anunciantes y potenciales compradores o arrendatarios.
                    </li>
                    <li>
                      <strong>Promocionar propiedades</strong> a través de nuestras redes sociales, boletines informativos 
                      o plataformas asociadas cuando el anunciante así lo autorice.
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Datos privados</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>
                      <strong>Personalizar la experiencia del usuario</strong>: adaptar los resultados de búsqueda 
                      y recomendaciones según las preferencias e historial del usuario.
                    </li>
                    <li>
                      <strong>Generar estadísticas e informes</strong> para mejorar nuestros servicios y ofrecer 
                      información relevante a los anunciantes sobre el rendimiento de sus anuncios.
                    </li>
                    <li>
                      <strong>Garantizar la seguridad</strong> de la plataforma mediante la detección y prevención 
                      de actividades fraudulentas o mal uso.
                    </li>
                    <li>
                      <strong>Comunicaciones relevantes</strong>: enviar notificaciones sobre nuevas propiedades que coincidan 
                      con criterios de búsqueda guardados, actualizaciones de la plataforma o información sobre servicios 
                      relacionados (con opción de desuscripción).
                    </li>
                    <li>
                      <strong>Soporte al cliente</strong>: responder a consultas, resolver problemas y mejorar nuestros servicios.
                    </li>
                    <li>
                      <strong>Cumplimiento legal</strong>: cumplir con obligaciones legales, responder a procedimientos legales 
                      y proteger nuestros derechos.
                    </li>
                  </ul>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mt-6">
                    <p className="text-yellow-800">
                      <strong>Nota importante</strong>: Utilizamos su información exclusivamente para los fines especificados en esta 
                      política. No vendemos su información personal a terceros para fines de marketing o publicidad.
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="seguridad">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                    Seguridad de la Información
                  </h2>
                  <p className="text-gray-700">
                    La seguridad de su información es una prioridad para InKayros. Implementamos medidas técnicas, 
                    administrativas y físicas diseñadas para proteger sus datos contra accesos no autorizados, 
                    pérdidas o alteraciones:
                  </p>
                  <ul className="list-disc pl-6 mt-4 space-y-2 text-gray-700">
                    <li>
                      <strong>Encriptación de datos sensibles</strong>: Utilizamos encriptación SSL/TLS para la transmisión 
                      de datos y algoritmos de encriptación para almacenar información sensible como contraseñas y datos de 
                      autenticación.
                    </li>
                    <li>
                      <strong>Acceso restringido</strong>: El acceso a la información personal está limitado a empleados 
                      autorizados que necesitan conocer esa información para operar, desarrollar o mejorar nuestros 
                      servicios.
                    </li>
                    <li>
                      <strong>Monitoreo continuo</strong>: Nuestros sistemas son monitoreados regularmente para detectar 
                      posibles vulnerabilidades y ataques.
                    </li>
                    <li>
                      <strong>Actualizaciones regulares</strong>: Mantenemos nuestros sistemas actualizados con los 
                      últimos parches de seguridad.
                    </li>
                    <li>
                      <strong>Copias de seguridad</strong>: Realizamos copias de seguridad periódicas para prevenir 
                      la pérdida de datos.
                    </li>
                    <li>
                      <strong>Protocolos de respuesta</strong>: Contamos con protocolos establecidos para responder 
                      a incidentes de seguridad.
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Datos encriptados vs. no encriptados</h3>
                  <p className="text-gray-700 mt-2">
                    Para mayor transparencia, especificamos qué datos están encriptados y cuáles no:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                      <h4 className="font-medium text-green-800 mb-2">Datos encriptados</h4>
                      <ul className="list-disc pl-6 space-y-1 text-green-700">
                        <li>Contraseñas (hash + salt)</li>
                        <li>Datos de autenticación</li>
                        <li>Mensajes privados entre usuarios</li>
                        <li>Información de verificación de identidad</li>
                        <li>Datos parciales de medios de pago</li>
                      </ul>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h4 className="font-medium text-gray-800 mb-2">Datos no encriptados</h4>
                      <ul className="list-disc pl-6 space-y-1 text-gray-700">
                        <li>Información pública de propiedades</li>
                        <li>Nombres de usuario y perfiles públicos</li>
                        <li>Reseñas y calificaciones</li>
                        <li>Datos estadísticos y analíticos</li>
                        <li>Registros de actividad básicos</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-6">
                    <p className="text-blue-800">
                      <strong>Importante</strong>: A pesar de nuestras medidas de seguridad, ninguna transmisión de datos por 
                      Internet o sistema de almacenamiento electrónico puede garantizarse como 100% seguro. Si tiene motivos 
                      para creer que su interacción con nosotros ya no es segura, notifíquenos inmediatamente.
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="compartir">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Share2 className="h-6 w-6 text-blue-600" />
                    Compartir Información
                  </h2>
                  <p className="text-gray-700">
                    InKayros se compromete a mantener la confidencialidad de su información personal. Sin embargo, 
                    existen circunstancias específicas en las que podemos compartir ciertos datos:
                  </p>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Datos que podemos compartir</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>
                      <strong>Proveedores de servicios</strong>: Compartimos información con terceros de confianza que nos 
                      ayudan a operar nuestro sitio web, conducir nuestro negocio, o brindarles servicios, siempre que 
                      estas partes acuerden mantener esta información confidencial. Estos proveedores incluyen servicios 
                      de alojamiento web, procesamiento de pagos, análisis de datos y atención al cliente.
                    </li>
                    <li>
                      <strong>Cumplimiento legal</strong>: Podemos divulgar información cuando creamos de buena fe que la 
                      divulgación es necesaria para cumplir con la ley, responder a órdenes judiciales, citaciones o proteger 
                      los derechos, la propiedad o la seguridad de InKayros, nuestros usuarios u otros.
                    </li>
                    <li>
                      <strong>Transferencias comerciales</strong>: En caso de fusión, adquisición o venta de activos, su 
                      información personal puede ser transferida. Notificaremos a los usuarios afectados antes de que la 
                      información personal esté sujeta a una política de privacidad diferente.
                    </li>
                    <li>
                      <strong>Datos estadísticos agregados</strong>: Podemos compartir información agregada y anonimizada 
                      para fines de análisis, investigación de mercado o mejora de servicios. Esta información no permite 
                      identificar a usuarios individuales.
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Datos que nunca compartimos</h3>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mt-2">
                    <p className="text-purple-800">
                      Nos comprometemos a <strong>nunca vender, alquilar o compartir</strong> con terceros no afiliados:
                    </p>
                    <ul className="list-disc pl-6 mt-2 space-y-1 text-purple-700">
                      <li>Mensajes privados entre usuarios</li>
                      <li>Contraseñas o datos de autenticación</li>
                      <li>Información financiera completa</li>
                      <li>Historiales de búsqueda individuales</li>
                      <li>Datos personales sensibles</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg mt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Transferencias internacionales de datos</h3>
                    <p className="text-gray-700">
                      InKayros opera principalmente en Perú, pero algunos de nuestros proveedores de servicios pueden estar ubicados 
                      en diferentes países. Al utilizar nuestros servicios, usted consiente la transferencia de su información a países 
                      fuera de su país de residencia, incluidos los Estados Unidos, que pueden tener reglas de protección de datos 
                      diferentes a las de su país. Cuando transferimos datos, implementamos salvaguardas adecuadas para garantizar 
                      que su información reciba un nivel de protección acorde con las leyes de protección de datos aplicables.
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="derechos">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <UserCheck className="h-6 w-6 text-blue-600" />
                    Derechos de los Usuarios
                  </h2>
                  <p className="text-gray-700">
                    En InKayros respetamos sus derechos sobre sus datos personales. De acuerdo con la Ley N° 29733, 
                    Ley de Protección de Datos Personales de Perú, y otras regulaciones aplicables, usted tiene los 
                    siguientes derechos:
                  </p>
                  
                  <div className="bg-indigo-50 p-6 rounded-xl mt-4 border border-indigo-100">
                    <h3 className="text-xl font-semibold text-indigo-800 mb-4">Derechos ARCO</h3>
                    <ul className="space-y-4">
                      <li className="flex gap-3">
                        <div className="bg-indigo-100 rounded-full h-8 w-8 flex-shrink-0 flex items-center justify-center">
                          <span className="text-indigo-800 font-semibold">A</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800">Derecho de Acceso</h4>
                          <p className="text-indigo-700 text-sm">
                            Derecho a solicitar y obtener información sobre sus datos personales que tratamos, su origen 
                            y las finalidades del tratamiento.
                          </p>
                        </div>
                      </li>
                      
                      <li className="flex gap-3">
                        <div className="bg-indigo-100 rounded-full h-8 w-8 flex-shrink-0 flex items-center justify-center">
                          <span className="text-indigo-800 font-semibold">R</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800">Derecho de Rectificación</h4>
                          <p className="text-indigo-700 text-sm">
                            Derecho a solicitar la corrección de sus datos personales cuando sean inexactos, incompletos, 
                            desactualizados o inadecuados.
                          </p>
                        </div>
                      </li>
                      
                      <li className="flex gap-3">
                        <div className="bg-indigo-100 rounded-full h-8 w-8 flex-shrink-0 flex items-center justify-center">
                          <span className="text-indigo-800 font-semibold">C</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800">Derecho de Cancelación</h4>
                          <p className="text-indigo-700 text-sm">
                            Derecho a solicitar la supresión o cancelación de sus datos personales de nuestras bases de datos 
                            cuando hayan dejado de ser necesarios para las finalidades para las cuales fueron recopilados.
                          </p>
                        </div>
                      </li>
                      
                      <li className="flex gap-3">
                        <div className="bg-indigo-100 rounded-full h-8 w-8 flex-shrink-0 flex items-center justify-center">
                          <span className="text-indigo-800 font-semibold">O</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-indigo-800">Derecho de Oposición</h4>
                          <p className="text-indigo-700 text-sm">
                            Derecho a oponerse al tratamiento de sus datos personales cuando no haya otorgado su consentimiento 
                            o existan motivos legítimos para ello.
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Derechos adicionales</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>
                      <strong>Derecho a retirar el consentimiento</strong>: Puede retirar su consentimiento en cualquier 
                      momento para aquellos tratamientos basados en su consentimiento.
                    </li>
                    <li>
                      <strong>Derecho a la portabilidad</strong>: En ciertos casos, puede solicitar recibir sus datos 
                      en un formato estructurado, de uso común y lectura mecánica.
                    </li>
                    <li>
                      <strong>Derecho a no ser objeto de decisiones automatizadas</strong>: Puede oponerse a ser 
                      objeto de decisiones basadas únicamente en tratamientos automatizados que produzcan efectos 
                      jurídicos o le afecten significativamente.
                    </li>
                    <li>
                      <strong>Derecho a presentar una reclamación</strong>: Tiene derecho a presentar una reclamación 
                      ante la Autoridad Nacional de Protección de Datos Personales de Perú.
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">¿Cómo ejercer sus derechos?</h3>
                  <p className="text-gray-700 mt-2">
                    Para ejercer cualquiera de sus derechos, puede:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Enviar un correo electrónico a <span className="text-blue-600">privacidad@inkayros.com</span></li>
                    <li>Utilizar la sección "Configuración de privacidad" en su perfil de usuario</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    Haremos todo lo posible para responder a su solicitud dentro de los 10 días hábiles siguientes a su recepción. 
                    En algunos casos, podríamos necesitar información adicional para verificar su identidad y procesar su solicitud.
                  </p>

                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg mt-6">
                    <p className="text-yellow-800">
                      <strong>Nota importante</strong>: En ciertos casos, es posible que no podamos eliminar completamente 
                      su información debido a obligaciones legales, seguridad, prevención de fraude, o resolución de disputas.
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="cookies">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Cookie className="h-6 w-6 text-blue-600" />
                    Cookies y Tecnologías Similares
                  </h2>
                  <p className="text-gray-700">
                    InKayros utiliza cookies y tecnologías similares para mejorar su experiencia en nuestra plataforma. Las cookies son pequeños 
                    archivos de texto que se almacenan en su dispositivo cuando visita nuestro sitio web.
                  </p>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Tipos de cookies que utilizamos</h3>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>
                      <strong>Cookies esenciales</strong>: Necesarias para el funcionamiento básico del sitio. Permiten navegar 
                      y utilizar características como áreas seguras, carrito de compras, etc.
                    </li>
                    <li>
                      <strong>Cookies de preferencias</strong>: Permiten recordar información que cambia el aspecto o comportamiento 
                      del sitio (p. ej., su idioma preferido o la región en la que se encuentra).
                    </li>
                    <li>
                      <strong>Cookies analíticas</strong>: Nos ayudan a entender cómo interactúan los visitantes con el sitio, 
                      recopilando y reportando información de forma anónima.
                    </li>
                    <li>
                      <strong>Cookies de marketing</strong>: Utilizadas para rastrear a los visitantes en los sitios web. La intención 
                      es mostrar anuncios relevantes y atractivos para el usuario individual.
                    </li>
                  </ul>

                  <h3 className="text-lg font-semibold text-gray-700 mt-6">Control de cookies</h3>
                  <p className="text-gray-700 mt-2">
                    Puede configurar su navegador para rechazar todas las cookies o para que le avise cuando se envía una cookie. 
                    Sin embargo, si rechaza las cookies, es posible que algunas partes de nuestro sitio web no funcionen correctamente.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Además, proporcionamos un panel de control de cookies accesible desde el pie de página de nuestro sitio web, 
                    donde puede ajustar sus preferencias de cookies en cualquier momento.
                  </p>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-6">
                    <h4 className="font-medium text-gray-800 mb-2">Otras tecnologías de seguimiento</h4>
                    <p className="text-gray-700">
                      Además de las cookies, podemos utilizar balizas web, píxeles de seguimiento y tecnologías similares 
                      para mejorar su experiencia y optimizar nuestros servicios. Estas tecnologías pueden transferir 
                      cierta información a su dispositivo para permitirnos, por ejemplo, entender cómo interactúa con 
                      nuestro sitio web o correos electrónicos.
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="cambios">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Clock className="h-6 w-6 text-blue-600" />
                    Cambios en la Política de Privacidad
                  </h2>
                  <p className="text-gray-700">
                    Podemos actualizar nuestra Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas 
                    de información o cambios en la legislación aplicable. Le recomendamos revisar esta página regularmente para 
                    obtener la información más reciente sobre nuestras prácticas de privacidad.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Cuando realicemos cambios materiales a esta Política de Privacidad:
                  </p>
                  <ul className="list-disc pl-6 mt-2 space-y-2 text-gray-700">
                    <li>Actualizaremos la fecha de "última actualización" en la parte superior de esta política.</li>
                    <li>Le notificaremos a través de un aviso destacado en nuestro sitio web o mediante un correo electrónico.</li>
                    <li>En algunos casos, podríamos solicitar su consentimiento antes de implementar los cambios.</li>
                  </ul>
                  <p className="text-gray-700 mt-4">
                    El uso continuado de nuestros servicios después de la publicación de los cambios a esta Política de Privacidad 
                    implica la aceptación de dichos cambios.
                  </p>

                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mt-6">
                    <p className="text-blue-800">
                      <strong>Importante</strong>: Si no está de acuerdo con los cambios realizados en esta política, puede dejar 
                      de utilizar nuestros servicios y solicitar la eliminación de su cuenta y datos personales conforme a los 
                      procedimientos descritos en la sección "Derechos de los Usuarios".
                    </p>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section id="contacto">
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <Mail className="h-6 w-6 text-blue-600" />
                    Información de Contacto
                  </h2>
                  <p className="text-gray-700">
                    Si tiene preguntas, inquietudes o sugerencias sobre esta Política de Privacidad o sobre el tratamiento 
                    de sus datos personales, puede ponerse en contacto con nuestro Delegado de Protección de Datos a través de:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 mt-4 border border-gray-200">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-blue-600 mt-1" />
                        <div>
                          <p className="text-gray-500 text-sm">Correo electrónico:</p>
                          <p className="text-gray-800 font-medium">privacidad@inkayros.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-blue-600 mt-1" />
                        <div>
                          <p className="text-gray-500 text-sm">Formulario web:</p>
                          <p className="text-gray-800 font-medium">www.inkayros.com/contacto</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mt-6">
                    También puede presentar una reclamación ante la Autoridad Nacional de Protección de Datos Personales de Perú:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 mt-4 border border-gray-200">
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <img 
                          src="/InKairos.ico" 
                          alt="InKayros Logo" 
                          className="w-12 h-12 mt-1" 
                        />
                        <div>
                          <p className="text-gray-500 text-sm">Autoridad Nacional de Protección de Datos Personales</p>
                          <p className="text-gray-800 font-medium">Ministerio de Justicia y Derechos Humanos</p>
                          <p className="text-gray-700 text-sm mt-1">Scipión Llona 350, Miraflores, Lima 18 - Perú</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="border-t border-gray-200 my-8"></div>

                <section className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">Declaración Final</h2>
                  <p className="text-gray-700">
                    Esta Política de Privacidad fue diseñada para proporcionarle información clara y transparente sobre 
                    cómo recopilamos, utilizamos y protegemos sus datos personales. Al utilizar nuestros servicios, usted 
                    reconoce que ha leído y comprendido esta política.
                  </p>
                  <p className="text-gray-700 mt-4">
                    Nos comprometemos a respetar su privacidad y a tratar sus datos personales con la máxima responsabilidad 
                    y confidencialidad, de acuerdo con la legislación aplicable.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PoliticaPrivacidad;